import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { formatOverviewDate, formatOverviewUsd } from '../../lib/sensemaker-overview-format'
import {
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_BORDER,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_CELL_COLOR,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_BG,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_COLOR,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_ROW_BORDER,
  SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_ROW_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TABLE_COLUMNS,
  SENSEMAKER_OVERVIEW_PAYMENT_TABLE_EMPTY_COLOR,
  SENSEMAKER_OVERVIEW_PAYMENT_TABLE_EMPTY_MIN_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TABLE_RADIUS_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TILE_GAP_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TILE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TILE_LABEL_COLOR,
  SENSEMAKER_OVERVIEW_PAYMENT_TILE_LABEL_SIZE_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TILE_LABEL_TRACKING_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TILE_RADIUS_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TILE_STYLES,
  SENSEMAKER_OVERVIEW_PAYMENT_TILE_VALUE_COLOR,
  SENSEMAKER_OVERVIEW_PAYMENT_TILE_VALUE_LINE_HEIGHT_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TILE_VALUE_SIZE_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TILE_VALUE_TO_LABEL_GAP_PX,
  SENSEMAKER_OVERVIEW_PAYMENT_TILES_TO_TABLE_GAP_PX,
  SENSEMAKER_OVERVIEW_SECTION_TITLE_TO_CONTENT_GAP_PX,
  sensemakerOverviewSectionTitleSx,
  type SensemakerOverviewPaymentTileVariant,
} from '../../lib/sensemaker-overview-layout'
import type {
  SensemakerPaymentMock,
  SensemakerPaymentRow,
} from '../../lib/sensemaker-overview-payment.mock'

interface Props {
  payment: SensemakerPaymentMock
}

const paymentTableHeaderSx = {
  display: 'grid',
  gridTemplateColumns: SENSEMAKER_OVERVIEW_PAYMENT_TABLE_COLUMNS,
  alignItems: 'center',
  minHeight: `${SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_HEIGHT_PX}px`,
  bgcolor: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_BG,
  borderBottom: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_ROW_BORDER,
  px: '24px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px',
  letterSpacing: '0.6px',
  textTransform: 'uppercase' as const,
  color: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_HEADER_COLOR,
}

function SummaryTile({
  label,
  value,
  variant,
}: Readonly<{
  label: string
  value: string
  variant: SensemakerOverviewPaymentTileVariant
}>) {
  const tileStyle = SENSEMAKER_OVERVIEW_PAYMENT_TILE_STYLES[variant]

  return (
    <Box
      data-testid={`sensemaker-payment-summary-${variant}`}
      sx={{
        flex: { xs: '1 1 100%', md: '1 1 50%' },
        minWidth: { xs: '100%', md: 0 },
        maxWidth: { md: '50%' },
        height: `${SENSEMAKER_OVERVIEW_PAYMENT_TILE_HEIGHT_PX}px`,
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${tileStyle.border}`,
        borderRadius: `${SENSEMAKER_OVERVIEW_PAYMENT_TILE_RADIUS_PX}px`,
        bgcolor: tileStyle.bg,
        pl: `${tileStyle.paddingLeftPx}px`,
        pr: `${tileStyle.paddingRightPx}px`,
        py: '16px',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${SENSEMAKER_OVERVIEW_PAYMENT_TILE_VALUE_TO_LABEL_GAP_PX}px`,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: `${SENSEMAKER_OVERVIEW_PAYMENT_TILE_VALUE_SIZE_PX}px`,
            fontWeight: 600,
            lineHeight: `${SENSEMAKER_OVERVIEW_PAYMENT_TILE_VALUE_LINE_HEIGHT_PX}px`,
            color: SENSEMAKER_OVERVIEW_PAYMENT_TILE_VALUE_COLOR,
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: `${SENSEMAKER_OVERVIEW_PAYMENT_TILE_LABEL_SIZE_PX}px`,
            fontWeight: 500,
            lineHeight: `${tileStyle.labelLineHeightPx}px`,
            letterSpacing: `${SENSEMAKER_OVERVIEW_PAYMENT_TILE_LABEL_TRACKING_PX}px`,
            color: SENSEMAKER_OVERVIEW_PAYMENT_TILE_LABEL_COLOR,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  )
}

function PaymentHistoryRow({ row }: Readonly<{ row: SensemakerPaymentRow }>) {
  return (
    <Box
      data-testid={`sensemaker-payment-row-${row.id}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: SENSEMAKER_OVERVIEW_PAYMENT_TABLE_COLUMNS,
        alignItems: 'center',
        minHeight: `${SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_ROW_HEIGHT_PX}px`,
        px: '24px',
        borderBottom: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_ROW_BORDER,
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        lineHeight: '20px',
        color: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_CELL_COLOR,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 400 }}>
        {formatOverviewDate(row.date)}
      </Typography>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 400 }}>
          {row.type}
        </Typography>
        {row.type === 'Instrument Charges' && row.breakdown && (
          <Tooltip
            title={
              <Box>
                <div>Instrument Cost: {formatOverviewUsd(row.breakdown.instrumentCostUsd)}</div>
                <div>Publishing Cost: {formatOverviewUsd(row.breakdown.publishingCostUsd)}</div>
                <div>Platform Charge: {formatOverviewUsd(row.breakdown.platformChargeUsd)}</div>
              </Box>
            }
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 16, color: '#6b7280', cursor: 'help' }}
              data-testid={`sensemaker-payment-breakdown-${row.id}`}
            />
          </Tooltip>
        )}
      </Box>
      <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 400 }}>
        {formatOverviewUsd(row.amountUsd)}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'inherit',
          fontSize: 'inherit',
          fontWeight: 500,
          color: row.status === 'Payment Failed' ? '#ef4444' : '#15803d',
        }}
      >
        {row.status}
      </Typography>
    </Box>
  )
}

function PaymentHistoryTable({ rows }: Readonly<{ rows: SensemakerPaymentRow[] }>) {
  return (
    <Box
      data-testid="sensemaker-payment-history-table"
      sx={{
        border: SENSEMAKER_OVERVIEW_INSTRUMENT_TABLE_BORDER,
        borderRadius: `${SENSEMAKER_OVERVIEW_PAYMENT_TABLE_RADIUS_PX}px`,
        overflow: 'hidden',
        bgcolor: '#fff',
      }}
    >
      <Box sx={paymentTableHeaderSx}>
        <span>date</span>
        <span>type</span>
        <span>amount</span>
        <span>status</span>
      </Box>
      {rows.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: `${SENSEMAKER_OVERVIEW_PAYMENT_TABLE_EMPTY_MIN_HEIGHT_PX}px`,
            px: '24px',
            py: '20px',
          }}
          data-testid="sensemaker-payment-history-empty"
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              color: SENSEMAKER_OVERVIEW_PAYMENT_TABLE_EMPTY_COLOR,
              textAlign: 'center',
            }}
          >
            No payment history yet. Transactions will show up here once processing begins.
          </Typography>
        </Box>
      ) : (
        rows.map((row) => <PaymentHistoryRow key={row.id} row={row} />)
      )}
    </Box>
  )
}

export function SensemakerOverviewPaymentSection({ payment }: Readonly<Props>) {
  const summaryTiles: {
    label: string
    value: string
    variant: SensemakerOverviewPaymentTileVariant
  }[] = [
    {
      label: 'Package Amount Purchased',
      value:
        payment.packageAmountPurchasedUsd == null
          ? 'N/A'
          : formatOverviewUsd(payment.packageAmountPurchasedUsd),
      variant: 'package',
    },
    {
      label: 'Total Instrument Cost to Date',
      value: formatOverviewUsd(payment.totalInstrumentCostUsd),
      variant: 'instrumentCost',
    },
  ]

  return (
    <Box data-testid="sensemaker-overview-payment">
      <Typography
        component="h2"
        sx={{
          ...sensemakerOverviewSectionTitleSx,
          mb: `${SENSEMAKER_OVERVIEW_SECTION_TITLE_TO_CONTENT_GAP_PX}px`,
        }}
      >
        Payment History & Information
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: `${SENSEMAKER_OVERVIEW_PAYMENT_TILE_GAP_PX}px`,
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          mb: `${SENSEMAKER_OVERVIEW_PAYMENT_TILES_TO_TABLE_GAP_PX}px`,
        }}
      >
        {summaryTiles.map((tile) => (
          <SummaryTile
            key={tile.variant}
            label={tile.label}
            value={tile.value}
            variant={tile.variant}
          />
        ))}
      </Box>
      <PaymentHistoryTable rows={payment.rows} />
    </Box>
  )
}
